import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Scale, Send, AlertTriangle, CheckCircle, Clock, Eye, EyeOff,
  Download, Save, Pencil, Trash2, DollarSign, Trophy, Shield, RefreshCw,
  Radio, Users, Calendar, Plus, ArrowRightLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { Spinner } from './ChallengeShared';
import { UserAvatar } from '../UserAvatar';
import { Label } from '../ui/label';

// ─── Garde-fous de saisie ────────────────────────────────────────────────────
const MESURE_BOUNDS = { poids: [30, 300, ' kg'], mg: [3, 75, ' %'], mm: [20, 200, ' kg'], metabolic_age: [10, 100, ' ans'] };
const FIELD_LABELS = { poids: 'Poids', mg: 'Gras', mm: 'Muscle', metabolic_age: 'Âge mét.' };

function mesureErrors(vals, nom = '') {
  const errs = [];
  Object.entries(MESURE_BOUNDS).forEach(([f, [lo, hi, unit]]) => {
    const v = parseFloat(vals[f]);
    if (!isNaN(v) && (v < lo || v > hi)) errs.push(`${nom ? nom + ' — ' : ''}${FIELD_LABELS[f]} ${v}${unit} hors limites (${lo}–${hi})`);
  });
  return errs;
}

function variationWarnings(vals, ref, nom = '') {
  const warns = [];
  ['poids', 'mg', 'mm'].forEach(f => {
    const v = parseFloat(vals[f]);
    const r = parseFloat(ref?.[f]);
    if (!isNaN(v) && !isNaN(r) && r > 0) {
      const pct = Math.abs(v - r) / r * 100;
      if (pct > 5) warns.push(`${nom ? nom + ' — ' : ''}${FIELD_LABELS[f]}: ${r} → ${v} (±${Math.round(pct)}%)`);
    }
  });
  return warns;
}

// ─── Section: Pesee Intermediaire — Free-form measurement with custom date ──
function PeseeIntermediaire({ participants, onRefresh }) {
  const [selectedPid, setSelectedPid] = useState('');
  const [form, setForm] = useState({ poids: '', mg: '', mm: '', metabolic_age: '', date_mesure: '' });
  const [saving, setSaving] = useState(false);
  const [pesees, setPesees] = useState([]);
  const [loadingPesees, setLoadingPesees] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ poids: '', mg: '', mm: '', metabolic_age: '', date_mesure: '' });

  const fetchPesees = useCallback(async () => {
    try {
      const [mRes, dRes] = await Promise.all([
        apiClient.get('/challenge/pesees'),
        apiClient.get('/challenge/pesees-dates'),
      ]);
      setPesees(mRes.data);
      if (!form.date_mesure) {
        setForm(prev => ({ ...prev, date_mesure: dRes.data.today || new Date().toISOString().slice(0, 10) }));
      }
    } catch {} finally { setLoadingPesees(false); }
  }, []);

  useEffect(() => { fetchPesees(); }, [fetchPesees]);

  const handleSubmit = async () => {
    if (!selectedPid || !form.poids) { toast.error('Selectionne un participant et le poids'); return; }
    const errs = mesureErrors(form, selectedParticipant?.nom);
    if (errs.length) { toast.error(errs.join(' · ')); return; }
    const ref = { poids: selectedParticipant?.poids_j0, mg: selectedParticipant?.mg_j0, mm: selectedParticipant?.mm_j0 };
    const warns = variationWarnings(form, ref, selectedParticipant?.nom);
    if (warns.length && !window.confirm(`⚠️ Variation inhabituelle (>5%) par rapport au J0 :\n\n${warns.join('\n')}\n\nConfirmer la saisie ?`)) return;
    setSaving(true);
    try {
      const { data } = await apiClient.post(`/challenge/mesures/${selectedPid}`, {
        poids: parseFloat(form.poids), mg: parseFloat(form.mg) || 0, mm: parseFloat(form.mm) || 0,
        metabolic_age: form.metabolic_age ? parseInt(form.metabolic_age) : undefined,
        date_mesure: form.date_mesure || undefined,
        type_mesure: 'controle'
      });
      const syncMsg = data.synced_health_metrics ? ' + synchro profil' : '';
      toast.success(`Pesee intermediaire enregistree !${syncMsg}`);
      setForm(prev => ({ ...prev, poids: '', mg: '', mm: '', metabolic_age: '' }));
      fetchPesees();
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette pesee ?')) return;
    try { await apiClient.delete(`/challenge/mesures/${id}`); toast.success('Supprimee'); fetchPesees(); onRefresh(); }
    catch { toast.error('Erreur'); }
  };

  const handleSaveEdit = async (id) => {
    const errs = mesureErrors(editForm);
    if (errs.length) { toast.error(errs.join(' · ')); return; }
    try {
      await apiClient.put(`/challenge/mesures/${id}`, {
        poids: parseFloat(editForm.poids), mg: parseFloat(editForm.mg), mm: parseFloat(editForm.mm),
        metabolic_age: editForm.metabolic_age ? parseInt(editForm.metabolic_age) : undefined,
        date_mesure: editForm.date_mesure
      });
      toast.success('Mesure modifiee');
      setEditingId(null);
      fetchPesees();
      onRefresh();
    } catch { toast.error('Erreur'); }
  };

  const selectedParticipant = participants.find(p => p.id === selectedPid);

  return (
    <div className="space-y-4" data-testid="pesee-intermediaire-section">
      {/* Info */}
      <div className="bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-lg p-2.5 flex items-center gap-2">
        <Calendar className="w-4 h-4 text-[#FF6B35] flex-shrink-0" />
        <p className="text-[11px] text-zinc-400">Pesee <strong className="text-[#FF6B35]">intermediaire</strong> — choisissez une date libre et saisissez les mesures d'un participant. Synchronisee automatiquement.</p>
      </div>

      {/* Form */}
      <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg p-4 space-y-3">
        <h3 className="text-sm font-bold text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Nouvelle pesee intermediaire</h3>

        <select value={selectedPid} onChange={e => setSelectedPid(e.target.value)}
          className="w-full bg-[#0F0F0F] border border-[#2A2A2E] text-white rounded-md px-3 py-2.5 text-sm" data-testid="inter-select-participant">
          <option value="">Choisir un participant...</option>
          {participants.map(p => <option key={p.id} value={p.id}>{p.nom}</option>)}
        </select>

        <div>
          <Label className="text-[#AAAAAA] text-xs flex items-center gap-1 mb-1"><Calendar className="w-3 h-3" /> Date de pesee</Label>
          <Input type="date" value={form.date_mesure} onChange={e => setForm({...form, date_mesure: e.target.value})}
            className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono text-sm" data-testid="inter-date-input" />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-[#AAAAAA] text-xs">Poids (kg)</Label><Input type="number" step="0.1" value={form.poids} onChange={e => setForm({...form, poids: e.target.value})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono" data-testid="inter-poids-input" /></div>
          <div><Label className="text-[#AAAAAA] text-xs">Gras (%)</Label><Input type="number" step="0.1" value={form.mg} onChange={e => setForm({...form, mg: e.target.value})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono" data-testid="inter-mg-input" /></div>
          <div><Label className="text-[#AAAAAA] text-xs">Muscle (kg)</Label><Input type="number" step="0.1" value={form.mm} onChange={e => setForm({...form, mm: e.target.value})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono" data-testid="inter-mm-input" /></div>
          <div><Label className="text-[#AAAAAA] text-xs">Âge métab. (ans)</Label><Input type="number" value={form.metabolic_age} onChange={e => setForm({...form, metabolic_age: e.target.value})} placeholder="Auto si vide" className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono" data-testid="inter-metabolic-age-input" /></div>
        </div>

        {selectedParticipant && (
          <div className="bg-[#0F0F0F] border border-[#2A2A2E] rounded-md p-2.5 text-xs text-zinc-500">
            J0 : {selectedParticipant.poids_j0}kg · {selectedParticipant.mg_j0}% · {selectedParticipant.mm_j0}kg MM
          </div>
        )}

        <Button onClick={handleSubmit} disabled={saving} className="w-full bg-[#FF6B35] text-white hover:bg-[#FF8C55] h-11" data-testid="inter-submit-btn">
          <Save className="w-4 h-4 mr-1.5" />{saving ? 'Enregistrement...' : 'Enregistrer & synchroniser'}
        </Button>
      </div>

      {/* History */}
      <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg p-4">
        <h3 className="text-sm font-bold text-white mb-3" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Historique pesees ({pesees.length})</h3>
        {loadingPesees ? <Spinner /> : (
          <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
            {pesees.map(m => (
              <div key={m.id} className="bg-[#0F0F0F] rounded-lg border border-[#2A2A2E]">
                {editingId === m.id ? (
                  <div className="p-3 space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <Input type="number" step="0.1" value={editForm.poids} onChange={e => setEditForm({...editForm, poids: e.target.value})} className="bg-[#1B1B1B] border-[#2A2A2E] text-white font-mono text-xs" placeholder="Poids" />
                      <Input type="number" step="0.1" value={editForm.mg} onChange={e => setEditForm({...editForm, mg: e.target.value})} className="bg-[#1B1B1B] border-[#2A2A2E] text-white font-mono text-xs" placeholder="MG%" />
                      <Input type="number" step="0.1" value={editForm.mm} onChange={e => setEditForm({...editForm, mm: e.target.value})} className="bg-[#1B1B1B] border-[#2A2A2E] text-white font-mono text-xs" placeholder="MM" />
                      <Input type="number" value={editForm.metabolic_age} onChange={e => setEditForm({...editForm, metabolic_age: e.target.value})} className="bg-[#1B1B1B] border-[#2A2A2E] text-white font-mono text-xs" placeholder="Âge métab." />
                    </div>
                    <Input type="date" value={editForm.date_mesure} onChange={e => setEditForm({...editForm, date_mesure: e.target.value})} className="bg-[#1B1B1B] border-[#2A2A2E] text-white font-mono text-xs" />
                    <div className="flex gap-2">
                      <Button onClick={() => handleSaveEdit(m.id)} size="sm" className="bg-[#2e7d32] text-white flex-1"><Save className="w-3 h-3 mr-1" />OK</Button>
                      <Button onClick={() => setEditingId(null)} size="sm" variant="outline" className="border-[#2A2A2E] text-zinc-400">Annuler</Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 p-2.5">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-white font-medium truncate">{m.participant_nom}</p>
                      <p className="text-[10px] text-zinc-500">{m.date_mesure}{m.saisi_par ? ` · saisi par ${m.saisi_par}` : ''}</p>
                    </div>
                    <div className="flex gap-2 font-mono text-[11px] flex-shrink-0">
                      <span className="text-white">{m.poids}kg</span>
                      <span className="text-zinc-400">{m.mg}%</span>
                      <span className="text-zinc-400">{m.mm}kg</span>
                      {m.metabolic_age && <span className="text-[#FFB800]">{m.metabolic_age}a</span>}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={() => { setEditingId(m.id); setEditForm({ poids: m.poids, mg: m.mg, mm: m.mm, metabolic_age: m.metabolic_age ?? '', date_mesure: m.date_mesure }); }}
                        className="w-7 h-7 flex items-center justify-center rounded bg-[#1B1B1B] text-zinc-500 hover:text-white"><Pencil className="w-3 h-3" /></button>
                      <button onClick={() => handleDelete(m.id)}
                        className="w-7 h-7 flex items-center justify-center rounded bg-[#1B1B1B] text-zinc-500 hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {pesees.length === 0 && <p className="text-center text-zinc-500 text-sm py-4">Aucune pesee intermediaire</p>}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── WebSocket Hook for live palier progress ────────────────────────────────
function usePalierLive(palierDate) {
  const [liveData, setLiveData] = useState(null);
  const wsRef = useRef(null);
  const pingRef = useRef(null);

  useEffect(() => {
    if (!palierDate) return;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsUrl = backendUrl.replace(/^http/, 'ws') + `/api/challenge/palier-live/${palierDate}`;

    let ws;
    try {
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onopen = () => {
        pingRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send('ping');
        }, 25000);
      };
      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data.type === 'progress' || data.type === 'meta') setLiveData(prev => ({ ...prev, ...data }));
        } catch {}
      };
      ws.onclose = () => { clearInterval(pingRef.current); };
      ws.onerror = () => {};
    } catch {}

    return () => {
      clearInterval(pingRef.current);
      if (ws && ws.readyState <= 1) ws.close();
    };
  }, [palierDate]);

  return liveData;
}

// ─── Live Indicator Component ───────────────────────────────────────────────
function LiveIndicator({ liveData }) {
  if (!liveData) return null;
  return (
    <div className="flex items-center gap-3 px-3 py-2 bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-lg" data-testid="live-indicator">
      <span className="relative flex h-2.5 w-2.5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF6B35] opacity-75" />
        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#FF6B35]" />
      </span>
      <span className="font-mono text-xs text-[#FF6B35] font-bold">LIVE</span>
      <span className="font-mono text-xs text-white">{liveData.filled ?? 0}/{liveData.total ?? '?'}</span>
      <span className="text-[9px] text-zinc-500">peses</span>
      {(liveData.admins_connected || 0) > 1 && (
        <span className="ml-auto flex items-center gap-1 text-[9px] text-zinc-400">
          <Users className="w-3 h-3" /> {liveData.admins_connected} admin{liveData.admins_connected > 1 ? 's' : ''}
        </span>
      )}
    </div>
  );
}

// ─── Section 1: Saisie des Paliers ─────────────────────────────────────────
function SaisiePaliers({ participants, paliers, palierDates, onRefresh }) {
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedLabel, setSelectedLabel] = useState('');
  const [rows, setRows] = useState({});
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [modifiedCells, setModifiedCells] = useState(new Set()); // Track modified cells for published paliers

  // WebSocket live progress
  const liveData = usePalierLive(selectedDate || null);

  // Paliers officiels uniquement (S2/S6/S10 = pesées intermédiaires, gérées à part)
  const OFFICIAL_LABELS = ['J0', 'S4', 'S8', 'S12'];
  const officialDates = palierDates.filter(d => OFFICIAL_LABELS.includes(d.label));

  // Find existing palier for selected date
  const existingPalier = paliers.find(p => p.date_palier === selectedDate);
  const isPublished = existingPalier?.published;

  useEffect(() => {
    if (officialDates.length > 0 && !selectedDate) {
      // Auto-select first unpublished or closest date
      const unpublished = officialDates.find(d => !paliers.find(p => p.date_palier === d.date && p.published));
      if (unpublished) { setSelectedDate(unpublished.date); setSelectedLabel(unpublished.label); }
      else { setSelectedDate(officialDates[0].date); setSelectedLabel(officialDates[0].label); }
    }
  }, [officialDates, paliers, selectedDate]);

  // Load rows from existing palier or init empty
  useEffect(() => {
    const newRows = {};
    participants.forEach(p => {
      const existing = existingPalier?.mesures?.[p.id];
      newRows[p.id] = {
        poids: existing?.poids ?? '',
        mg: existing?.mg ?? '',
        mm: existing?.mm ?? '',
        metabolic_age: existing?.metabolic_age ?? '',
      };
    });
    setRows(newRows);
    setModifiedCells(new Set());
  }, [participants, existingPalier]);

  const updateRow = (pid, field, value) => {
    setRows(prev => ({ ...prev, [pid]: { ...prev[pid], [field]: value } }));
    if (isPublished) {
      setModifiedCells(prev => new Set([...prev, `${pid}-${field}`]));
    }
  };

  const handleSave = async () => {
    const mesures = Object.entries(rows)
      .filter(([_, v]) => v.poids !== '' && v.poids !== null)
      .map(([pid, v]) => ({
        participant_id: pid,
        poids: parseFloat(v.poids) || null,
        mg: parseFloat(v.mg) || null,
        mm: parseFloat(v.mm) || null,
        metabolic_age: v.metabolic_age !== '' && v.metabolic_age != null ? parseInt(v.metabolic_age) : null,
      }));
    if (mesures.length === 0) { toast.error('Aucune mesure saisie'); return; }
    // Garde-fous: bornes + variation vs J0
    const allErrs = [];
    const allWarns = [];
    for (const m of mesures) {
      const p = participants.find(pp => pp.id === m.participant_id);
      allErrs.push(...mesureErrors(m, p?.nom));
      allWarns.push(...variationWarnings(m, { poids: p?.poids_j0, mg: p?.mg_j0, mm: p?.mm_j0 }, p?.nom));
    }
    if (allErrs.length) { toast.error(allErrs.slice(0, 4).join(' · ') + (allErrs.length > 4 ? ` (+${allErrs.length - 4})` : ''), { duration: 8000 }); return; }
    if (allWarns.length && !window.confirm(`⚠️ Variations inhabituelles (>5%) par rapport au J0 :\n\n${allWarns.slice(0, 10).join('\n')}${allWarns.length > 10 ? `\n(+${allWarns.length - 10} autres)` : ''}\n\nVerifie qu'il ne s'agit pas d'une faute de frappe. Confirmer l'enregistrement ?`)) return;
    setSaving(true);
    try {
      const { data } = await apiClient.post('/challenge/paliers/save', {
        date_palier: selectedDate, label: selectedLabel, mesures
      });
      toast.success(`${data.count} mesures enregistrees (brouillon)`);
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handlePublish = async () => {
    if (!existingPalier) { toast.error('Enregistre d\'abord les mesures'); return; }
    setPublishing(true);
    try {
      const { data } = await apiClient.put(`/challenge/paliers/${existingPalier.id}/publish`, { confirm: true });
      toast.success(`Palier ${selectedLabel} publie ! ${data.synced_mesures} mesures visibles.`);
      setConfirmPublish(false);
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setPublishing(false); }
  };

  const handleInlineEdit = async (pid, field, value) => {
    if (!existingPalier) return;
    const errs = mesureErrors({ [field]: value }, participants.find(pp => pp.id === pid)?.nom);
    if (errs.length) { toast.error(errs.join(' · ')); return; }
    const oldVal = parseFloat(existingPalier?.mesures?.[pid]?.[field]);
    const newVal = parseFloat(value);
    if (!isNaN(oldVal) && !isNaN(newVal) && oldVal > 0 && Math.abs(newVal - oldVal) / oldVal > 0.05) {
      if (!window.confirm(`⚠️ Variation inhabituelle : ${FIELD_LABELS[field]} ${oldVal} → ${newVal}. Confirmer ?`)) return;
    }
    try {
      await apiClient.put(`/challenge/paliers/${existingPalier.id}/mesure/${pid}`, { [field]: parseFloat(value) });
      toast.success('Modifie');
      setModifiedCells(prev => { const n = new Set(prev); n.delete(`${pid}-${field}`); return n; });
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  // Save all modified cells at once for published palier
  const handleSaveAllModified = async () => {
    if (!existingPalier || modifiedCells.size === 0) return;
    // Garde-fous: bornes + variation vs valeur precedente
    const allErrs = [];
    const allWarns = [];
    for (const key of modifiedCells) {
      const [mpid, mfield] = key.split('-');
      const p = participants.find(pp => pp.id === mpid);
      allErrs.push(...mesureErrors({ [mfield]: rows[mpid]?.[mfield] }, p?.nom));
      const oldVal = parseFloat(existingPalier?.mesures?.[mpid]?.[mfield]);
      const newVal = parseFloat(rows[mpid]?.[mfield]);
      if (!isNaN(oldVal) && !isNaN(newVal) && oldVal > 0 && Math.abs(newVal - oldVal) / oldVal > 0.05) {
        allWarns.push(`${p?.nom || ''} — ${FIELD_LABELS[mfield] || mfield}: ${oldVal} → ${newVal}`);
      }
    }
    if (allErrs.length) { toast.error(allErrs.slice(0, 4).join(' · '), { duration: 8000 }); return; }
    if (allWarns.length && !window.confirm(`⚠️ Variations inhabituelles (>5%) :\n\n${allWarns.join('\n')}\n\nVerifie qu'il ne s'agit pas d'une faute de frappe. Confirmer ?`)) return;
    setSaving(true);
    let savedCount = 0;
    try {
      // Group modifications by participant
      const modsByPid = {};
      for (const key of modifiedCells) {
        const [pid, field] = key.split('-');
        if (!modsByPid[pid]) modsByPid[pid] = {};
        modsByPid[pid][field] = parseFloat(rows[pid]?.[field]) || null;
      }
      // Save each participant's changes
      for (const [pid, fields] of Object.entries(modsByPid)) {
        await apiClient.put(`/challenge/paliers/${existingPalier.id}/mesure/${pid}`, fields);
        savedCount++;
      }
      toast.success(`${savedCount} participant(s) mis a jour`);
      setModifiedCells(new Set());
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const filledCount = Object.values(rows).filter(v => v.poids !== '' && v.poids !== null).length;

  return (
    <div className="space-y-4" data-testid="saisie-paliers-section">
      {/* Date selector — paliers officiels uniquement */}
      <div>
        <p className="text-xs text-zinc-500 mb-2 font-medium">Date de palier officiel (J0 · S4 · S8 · S12)</p>
        <div className="flex gap-1.5 flex-wrap">
          {officialDates.map(d => {
            const pal = paliers.find(p => p.date_palier === d.date);
            const pub = pal?.published;
            return (
              <button key={d.date} onClick={() => { setSelectedDate(d.date); setSelectedLabel(d.label); }}
                className={`px-3 py-2 rounded-lg text-xs font-bold transition-all ${
                  selectedDate === d.date
                    ? 'bg-[#FF6B35] text-white'
                    : pub ? 'bg-[#2e7d32]/20 text-[#2e7d32] border border-[#2e7d32]/30'
                    : 'bg-[#0F0F0F] border border-[#2A2A2E] text-zinc-400 hover:border-zinc-500'
                }`} data-testid={`palier-date-${d.label}`}>
                {d.label}
                <span className="text-[8px] opacity-70 block">{d.date.slice(5)}</span>
                {pub && <CheckCircle className="w-3 h-3 inline ml-0.5" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Live indicator (WebSocket) */}
      {!isPublished && selectedDate && <LiveIndicator liveData={liveData} />}

      {/* Status bar */}
      <div className={`flex items-center justify-between p-2.5 rounded-lg text-xs ${
        isPublished ? 'bg-[#2e7d32]/15 border border-[#2e7d32]/30 text-[#2e7d32]' : 'bg-[#FFB800]/10 border border-[#FFB800]/20 text-[#FFB800]'
      }`}>
        <div className="flex items-center gap-2">
          {isPublished ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          <span className="font-bold">{isPublished ? `Publie le ${existingPalier?.published_at?.slice(0, 10)}` : 'Brouillon — invisible aux membres'}</span>
        </div>
        <span className="font-mono">{filledCount}/{participants.length}</span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto -mx-4 px-4">
        <table className="w-full text-xs" data-testid="palier-table">
          <thead>
            <tr className="text-zinc-500 border-b border-[#2A2A2E]">
              <th className="text-left py-2 pl-2 w-[120px]">Challenger</th>
              <th className="text-center py-2 w-[80px]">Poids (kg)</th>
              <th className="text-center py-2 w-[80px]">Gras (%)</th>
              <th className="text-center py-2 w-[80px]">Muscle (kg)</th>
              <th className="text-center py-2 w-[70px]">Âge mét.</th>
            </tr>
          </thead>
          <tbody>
            {participants.map(p => (
              <tr key={p.id} className="border-b border-[#2A2A2E]/50 hover:bg-[#1B1B1B]">
                <td className="py-1.5 pl-2">
                  <div className="flex items-center gap-1.5">
                    <UserAvatar name={p.nom} photoPath={p.profile_photo} size="xs" isChallenger interactive={false} />
                    <div className="min-w-0">
                      <span className="text-white font-medium truncate text-[11px] block">{p.nom}</span>
                      {existingPalier?.mesures?.[p.id]?.saisi_par && (
                        <span className="text-[8px] text-zinc-600 block truncate" data-testid={`saisi-par-${p.nom}`}>saisi par {existingPalier.mesures[p.id].saisi_par}</span>
                      )}
                      {existingPalier?.mesures?.[p.id]?.reporte_fields?.length > 0 && (
                        <span className="text-[8px] text-[#FFB800] block truncate" data-testid={`reporte-${p.nom}`}>reporté: {existingPalier.mesures[p.id].reporte_fields.join(', ')}</span>
                      )}
                    </div>
                  </div>
                </td>
                {['poids', 'mg', 'mm', 'metabolic_age'].map(field => (
                  <td key={field} className="py-1.5 px-1">
                    <input type="number" step="0.1"
                      value={rows[p.id]?.[field] ?? ''}
                      onChange={e => updateRow(p.id, field, e.target.value)}
                      className={`w-full bg-[#0F0F0F] border rounded px-2 py-1.5 text-white font-mono text-center text-xs focus:border-[#FF6B35] focus:outline-none ${
                        modifiedCells.has(`${p.id}-${field}`) ? 'border-[#FF6B35]/60 bg-[#FF6B35]/5' : 'border-[#2A2A2E]'
                      }`}
                      placeholder="-"
                      data-testid={`cell-${p.nom}-${field}`}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Action buttons */}
      <div className="flex gap-2">
        {!isPublished && (
          <>
            <Button onClick={handleSave} disabled={saving} className="flex-1 bg-[#FF6B35] text-white hover:bg-[#FF8C55]" data-testid="save-palier-btn">
              <Save className="w-4 h-4 mr-1.5" />{saving ? 'Enregistrement...' : `Enregistrer (${filledCount})`}
            </Button>
            {existingPalier && (
              <Button onClick={() => setConfirmPublish(true)} className="flex-1 bg-[#2e7d32] text-white hover:bg-[#3a9e3e]" data-testid="publish-palier-btn">
                <Send className="w-4 h-4 mr-1.5" />Publier le palier
              </Button>
            )}
          </>
        )}
        {isPublished && (
          <div className="w-full space-y-2">
            {modifiedCells.size > 0 && (
              <Button onClick={handleSaveAllModified} disabled={saving} className="w-full bg-[#FF6B35] text-white hover:bg-[#FF8C55] h-11" data-testid="save-published-changes-btn">
                <Save className="w-4 h-4 mr-1.5" />{saving ? 'Enregistrement...' : `Sauvegarder les modifications (${modifiedCells.size})`}
              </Button>
            )}
            <div className="text-center text-xs text-[#2e7d32] font-bold py-1">
              <CheckCircle className="w-4 h-4 inline mr-1" /> Palier publie — modifiez les valeurs puis cliquez Sauvegarder
            </div>
          </div>
        )}
      </div>

      {/* Modification history */}
      {existingPalier?.modifications_history?.length > 0 && (
        <details className="text-xs">
          <summary className="text-zinc-500 cursor-pointer hover:text-zinc-400">Historique modifications ({existingPalier.modifications_history.length})</summary>
          <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
            {existingPalier.modifications_history.slice(-10).reverse().map((h, i) => (
              <div key={i} className="text-[10px] text-zinc-600 bg-[#0F0F0F] rounded p-1.5">
                <span className="text-zinc-400">{h.by}</span> a modifie <span className="text-white">{h.field}</span> : {h.old} → {h.new}
                <span className="text-zinc-700 ml-1">{h.at?.slice(0, 16)}</span>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* Move mesures between paliers */}
      <MovePalierTool paliers={paliers} onRefresh={onRefresh} />

      {/* Recover lost palier mesures from synced weigh-ins */}
      <RestorePalierTool paliers={paliers} onRefresh={onRefresh} />

      {/* Confirm publish modal */}
      {confirmPublish && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setConfirmPublish(false)}>
          <div className="bg-[#1B1B1B] rounded-xl p-6 max-w-sm w-full space-y-4" onClick={e => e.stopPropagation()} data-testid="confirm-publish-modal">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-[#FFB800]" />
              <h3 className="text-base font-bold text-white">Publier le palier {selectedLabel} ?</h3>
            </div>
            <p className="text-sm text-zinc-400">
              Les <strong className="text-white">{filledCount} mesures</strong> seront visibles par tous les membres.
              Cette action est <strong className="text-[#FFB800]">irreversible cote affichage</strong>.
            </p>
            <div className="flex gap-2">
              <Button onClick={() => setConfirmPublish(false)} variant="outline" className="flex-1 border-[#2A2A2E] text-zinc-400">Annuler</Button>
              <Button onClick={handlePublish} disabled={publishing} className="flex-1 bg-[#2e7d32] text-white hover:bg-[#3a9e3e]" data-testid="confirm-publish-btn">
                {publishing ? 'Publication...' : 'Confirmer la publication'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Outil: Déplacer les mesures d'un palier vers un autre ─────────────────
function MovePalierTool({ paliers, onRefresh }) {
  const [fromLabel, setFromLabel] = useState('');
  const [toLabel, setToLabel] = useState('');
  const [overwrite, setOverwrite] = useState(false);
  const [moving, setMoving] = useState(false);
  const LABELS = ['J0', 'S2', 'S4', 'S6', 'S8', 'S10', 'S12'];
  const sourceOptions = paliers.filter(p => Object.keys(p.mesures || {}).length > 0);

  const handleMove = async () => {
    if (!fromLabel || !toLabel) { toast.error('Choisis le palier source et le palier cible'); return; }
    if (fromLabel === toLabel) { toast.error('Source et cible identiques'); return; }
    const msg = overwrite
      ? `Déplacer TOUTES les mesures de ${fromLabel} vers ${toLabel} en ÉCRASANT les mesures existantes de ${toLabel} ?`
      : `Déplacer les mesures de ${fromLabel} vers ${toLabel} ? Les mesures déjà présentes dans ${toLabel} seront conservées (et resteront dans ${fromLabel}).`;
    if (!window.confirm(msg)) return;
    setMoving(true);
    try {
      const { data } = await apiClient.post('/challenge/paliers/move-mesures', { from_label: fromLabel, to_label: toLabel, overwrite });
      let m = `${data.moved} mesures déplacées de ${data.from} vers ${data.to}`;
      if (data.overwritten) m += ` (${data.overwritten} écrasées)`;
      if (data.skipped) m += ` — ${data.skipped} conservées dans ${data.from} (déjà présentes dans ${data.to})`;
      toast.success(m, { duration: 8000 });
      setFromLabel(''); setToLabel('');
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setMoving(false); }
  };

  return (
    <details className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg" data-testid="move-palier-tool">
      <summary className="p-3 text-xs font-bold text-zinc-400 cursor-pointer hover:text-white flex items-center gap-2">
        <ArrowRightLeft className="w-3.5 h-3.5 text-[#FF6B35]" /> Déplacer les mesures d'un palier vers un autre
      </summary>
      <div className="p-3 pt-0 space-y-2">
        <p className="text-[10px] text-zinc-500">Utile si des mesures ont été saisies dans le mauvais palier (ex: S4 au lieu de S2). Aucune mesure n'est jamais supprimée : en cas de conflit, elles restent dans le palier source sauf si vous cochez « Écraser ».</p>
        <div className="flex items-center gap-2">
          <select value={fromLabel} onChange={e => setFromLabel(e.target.value)}
            className="flex-1 bg-[#0F0F0F] border border-[#2A2A2E] rounded-md px-2 py-2 text-white text-xs" data-testid="move-from-select">
            <option value="">Source...</option>
            {sourceOptions.map(p => <option key={p.id} value={p.label}>{p.label} ({Object.keys(p.mesures || {}).length} mesures)</option>)}
          </select>
          <ArrowRightLeft className="w-4 h-4 text-zinc-600 flex-shrink-0" />
          <select value={toLabel} onChange={e => setToLabel(e.target.value)}
            className="flex-1 bg-[#0F0F0F] border border-[#2A2A2E] rounded-md px-2 py-2 text-white text-xs" data-testid="move-to-select">
            <option value="">Cible...</option>
            {LABELS.filter(l => l !== fromLabel).map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <label className="flex items-center gap-2 text-[11px] text-zinc-400 cursor-pointer" data-testid="move-overwrite-label">
          <input type="checkbox" checked={overwrite} onChange={e => setOverwrite(e.target.checked)} className="accent-[#FF6B35]" data-testid="move-overwrite-checkbox" />
          Écraser les mesures existantes dans la cible (les valeurs de la source gagnent)
        </label>
        <Button onClick={handleMove} disabled={moving || !fromLabel || !toLabel} className="w-full bg-[#FF6B35] text-white hover:bg-[#FF8C55] h-9 text-xs" data-testid="move-palier-btn">
          {moving ? 'Déplacement...' : `Déplacer ${fromLabel || '?'} → ${toLabel || '?'}`}
        </Button>
      </div>
    </details>
  );
}

// ─── Outil: Récupérer les mesures d'un palier depuis les pesées synchronisées ──
function RestorePalierTool({ paliers, onRefresh }) {
  const [label, setLabel] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const handleAnalyze = async () => {
    if (!label) { toast.error('Choisis un palier'); return; }
    setLoading(true); setReport(null);
    try {
      const { data } = await apiClient.post('/challenge/paliers/restore-mesures', { label, dry_run: true });
      setReport(data);
      if (data.to_restore === 0) toast.info('Aucune mesure à récupérer — le palier est déjà à jour');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setLoading(false); }
  };

  const handleApply = async () => {
    if (!window.confirm(`Restaurer ${report.to_restore} mesures dans le palier ${label} ?`)) return;
    setApplying(true);
    try {
      const { data } = await apiClient.post('/challenge/paliers/restore-mesures', { label, dry_run: false });
      toast.success(`${data.to_restore} mesures restaurées dans ${label}`, { duration: 8000 });
      setReport(null); setLabel('');
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setApplying(false); }
  };

  return (
    <details className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg" data-testid="restore-palier-tool">
      <summary className="p-3 text-xs font-bold text-zinc-400 cursor-pointer hover:text-white flex items-center gap-2">
        <RefreshCw className="w-3.5 h-3.5 text-[#2e7d32]" /> Récupérer les mesures perdues d'un palier
      </summary>
      <div className="p-3 pt-0 space-y-2">
        <p className="text-[10px] text-zinc-500">Reconstruit les mesures du palier depuis les pesées synchronisées (sauvegarde interne). Cliquez d'abord « Analyser » pour voir ce qui peut être récupéré — rien n'est modifié à cette étape.</p>
        <div className="flex items-center gap-2">
          <select value={label} onChange={e => { setLabel(e.target.value); setReport(null); }}
            className="flex-1 bg-[#0F0F0F] border border-[#2A2A2E] rounded-md px-2 py-2 text-white text-xs" data-testid="restore-label-select">
            <option value="">Palier...</option>
            {paliers.map(p => <option key={p.id} value={p.label}>{p.label} ({p.date_palier})</option>)}
          </select>
          <Button onClick={handleAnalyze} disabled={loading || !label} className="bg-[#2A2A2E] text-white hover:bg-[#3A3A3E] h-9 text-xs px-4" data-testid="restore-analyze-btn">
            {loading ? 'Analyse...' : 'Analyser'}
          </Button>
        </div>
        {report && report.to_restore > 0 && (
          <div className="space-y-1.5" data-testid="restore-report">
            <p className="text-[11px] text-[#FFB800] font-bold">{report.to_restore} mesures récupérables :</p>
            <div className="max-h-48 overflow-y-auto space-y-1">
              {report.report.map(r => (
                <div key={r.pid} className="bg-[#0F0F0F] rounded p-1.5 text-[10px] flex items-center justify-between gap-2">
                  <span className="text-white font-medium">{r.nom}</span>
                  <span className="font-mono text-zinc-500">
                    {r.current?.poids != null ? `${r.current.poids}kg` : '—'} → <span className="text-[#2e7d32]">{r.recovered.poids}kg · {r.recovered.mg}% · {r.recovered.mm}kg</span>
                  </span>
                </div>
              ))}
            </div>
            <Button onClick={handleApply} disabled={applying} className="w-full bg-[#2e7d32] text-white hover:bg-[#3a9e3e] h-9 text-xs" data-testid="restore-apply-btn">
              {applying ? 'Restauration...' : `Restaurer ces ${report.to_restore} mesures dans ${label}`}
            </Button>
          </div>
        )}
        {report && report.to_restore === 0 && (
          <p className="text-[11px] text-[#2e7d32]">✓ Palier à jour — rien à récupérer</p>
        )}
      </div>
    </details>
  );
}

// ─── Section 2: Controle ────────────────────────────────────────────────────
function ControleSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get('/challenge/admin-control').then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;
  if (!data) return null;

  return (
    <div className="space-y-3" data-testid="controle-section">
      <div className="flex items-center gap-2 mb-2">
        <Shield className="w-4 h-4 text-[#FF6B35]" />
        <h3 className="text-sm font-bold text-white">Officiel vs Declare</h3>
        {data.latest_palier && <span className="text-[9px] bg-[#FF6B35]/15 text-[#FF6B35] px-2 py-0.5 rounded-full font-bold">Dernier: {data.latest_palier}</span>}
      </div>
      <div className="overflow-x-auto -mx-4 px-4">
        <table className="w-full text-[10px]">
          <thead>
            <tr className="text-zinc-500 border-b border-[#2A2A2E]">
              <th className="text-left py-1.5 pl-2">Nom</th>
              <th className="text-center py-1.5">Officiel (kg)</th>
              <th className="text-center py-1.5">Declare (kg)</th>
              <th className="text-center py-1.5">Ecart</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map(r => (
              <tr key={r.pid} className={`border-b border-[#2A2A2E]/50 ${r.anomaly ? 'bg-[#c0392b]/10' : ''}`}>
                <td className="py-1.5 pl-2 text-white font-medium">{r.nom}</td>
                <td className="text-center font-mono text-[#FF6B35]">{r.official?.poids ?? '-'}</td>
                <td className="text-center font-mono text-[#3b82f6]">{r.declared?.poids ?? '-'}</td>
                <td className="text-center">
                  {r.official?.poids && r.declared?.poids ? (
                    <span className={`font-mono font-bold ${r.anomaly ? 'text-[#c0392b]' : 'text-zinc-500'}`}>
                      {(r.declared.poids - r.official.poids).toFixed(1)}kg
                      {r.anomaly && <AlertTriangle className="w-3 h-3 inline ml-0.5" />}
                    </span>
                  ) : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Section 3: Pronostics & Cautions Admin ─────────────────────────────────
function PronosticsCautionsAdmin({ onRefresh }) {
  const [allBets, setAllBets] = useState([]);
  const [pot, setPot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cautions, setCautions] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [betsRes, potRes, cautionsRes] = await Promise.allSettled([
        apiClient.get('/challenge/podium-bets'),
        apiClient.get('/challenge/podium-pot'),
        apiClient.get('/challenge/cautions'),
      ]);
      if (betsRes.status === 'fulfilled') setAllBets(betsRes.value.data);
      if (potRes.status === 'fulfilled') setPot(potRes.value.data);
      if (cautionsRes.status === 'fulfilled') setCautions(cautionsRes.value.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleConfirm = async (betId) => {
    try {
      await apiClient.put(`/challenge/podium-bet/${betId}/confirm`);
      toast.success('Paiement confirme');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (betId) => {
    if (!window.confirm('Supprimer ce pronostic ?')) return;
    try {
      await apiClient.delete(`/challenge/podium-bet/${betId}`);
      toast.success('Supprime');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  if (loading) return <Spinner />;

  const pending = allBets.filter(b => b.status === 'pending_payment');
  const confirmed = allBets.filter(b => b.status === 'confirmed');

  return (
    <div className="space-y-4" data-testid="pronostics-cautions-section">
      {/* Pot KPIs */}
      {pot && (
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="bg-[#0F0F0F] rounded-lg p-2">
            <p className="font-mono text-lg font-bold text-[#2e7d32]">{confirmed.length}</p>
            <p className="text-[9px] text-zinc-400">Confirmes</p>
          </div>
          <div className="bg-[#0F0F0F] rounded-lg p-2">
            <p className="font-mono text-lg font-bold text-[#FFB800]">{pending.length}</p>
            <p className="text-[9px] text-zinc-400">En attente</p>
          </div>
          <div className="bg-[#0F0F0F] rounded-lg p-2">
            <p className="font-mono text-lg font-bold text-white">{((pot?.pot_total || 0) / 1000).toFixed(0)}k</p>
            <p className="text-[9px] text-zinc-400">Pot XPF</p>
          </div>
        </div>
      )}

      {/* Pending payments */}
      {pending.length > 0 && (
        <div>
          <p className="text-xs text-[#FFB800] font-bold mb-2">Paiements en attente ({pending.length})</p>
          <div className="space-y-1.5">
            {pending.map(b => (
              <div key={b.id} className="flex items-center gap-2 p-2 bg-[#0F0F0F] rounded-lg">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-white truncate">{b.bettor_name} → {b.challenger_name}</p>
                  <p className="font-mono text-[10px] text-[#FFB800]">{b.amount?.toLocaleString()} XPF</p>
                </div>
                <Button size="sm" onClick={() => handleConfirm(b.id)} className="bg-[#2e7d32] text-white text-[10px] h-7">
                  <CheckCircle className="w-3 h-3 mr-1" />OK
                </Button>
                <button onClick={() => handleDelete(b.id)} className="text-red-400 hover:text-red-300 p-1">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All bets list */}
      {allBets.length > 0 && (
        <div>
          <p className="text-xs text-zinc-400 font-bold mb-2">Tous les pronostics ({allBets.length})</p>
          <div className="space-y-1 max-h-[300px] overflow-y-auto">
            {allBets.map(b => (
              <div key={b.id} className="flex items-center gap-2 px-2 py-1.5 bg-[#0F0F0F] rounded text-[11px]">
                <span className="text-white flex-1 truncate">{b.bettor_name}</span>
                <span className="text-zinc-500">→</span>
                <span className="text-white flex-1 truncate">{b.challenger_name}</span>
                <span className="font-mono text-[#FFB800]">{b.amount?.toLocaleString()}</span>
                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${
                  b.status === 'confirmed' ? 'bg-[#2e7d32]/20 text-[#2e7d32]' :
                  b.status === 'won' ? 'bg-[#FFB800]/20 text-[#FFB800]' :
                  b.status === 'lost' ? 'bg-zinc-800 text-zinc-500' :
                  'bg-[#FFB800]/20 text-[#FFB800]'
                }`}>{b.status === 'pending_payment' ? 'Attente' : b.status === 'confirmed' ? 'OK' : b.status}</span>
              </div>
            ))}
          </div>
          {/* Export buttons */}
          <div className="flex gap-2 mt-3">
            <ExportBtn format="csv" />
            <ExportBtn format="pdf" />
          </div>
        </div>
      )}

      {/* Cautions summary */}
      {cautions && (
        <div>
          <p className="text-xs text-zinc-400 font-bold mb-2">Cautions ({cautions.participants?.length || 0} challengers)</p>
          <div className="grid grid-cols-3 gap-2 text-center mb-3">
            <div className="bg-[#0F0F0F] rounded-lg p-2">
              <p className="font-mono text-sm font-bold text-[#2e7d32]">{((cautions.totaux?.vert || 0)/1000).toFixed(0)}k</p>
              <p className="text-[9px] text-zinc-400">Valide</p>
            </div>
            <div className="bg-[#0F0F0F] rounded-lg p-2">
              <p className="font-mono text-sm font-bold text-[#FFB800]">{((cautions.totaux?.total_engage || 0)/1000).toFixed(0)}k</p>
              <p className="text-[9px] text-zinc-400">Engage</p>
            </div>
            <div className="bg-[#0F0F0F] rounded-lg p-2">
              <p className="font-mono text-sm font-bold text-[#c0392b]">{((cautions.totaux?.rouge || 0)/1000).toFixed(0)}k</p>
              <p className="text-[9px] text-zinc-400">Perdu</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ExportBtn({ format }) {
  const [loading, setLoading] = useState(false);
  const handleExport = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/challenge/podium-bets/export-${format}`, { responseType: 'blob' });
      const blob = new Blob([data], { type: format === 'csv' ? 'text/csv' : 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `pronostics.${format}`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error('Erreur export'); }
    finally { setLoading(false); }
  };
  return (
    <button onClick={handleExport} disabled={loading} data-testid={`export-${format}`}
      className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-[#0F0F0F] border border-[#2A2A2E] rounded-lg text-[10px] font-semibold text-zinc-400 hover:text-white transition-colors">
      <Download className="w-3 h-3" />{loading ? '...' : format.toUpperCase()}
    </button>
  );
}

// ─── Main Admin Gestion View ────────────────────────────────────────────────
// ─── Anomalies de saisie (valeurs impossibles stockées) ─────────────────────
function AnomaliesCard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    apiClient.get('/challenge/mesures-anomalies').then(r => setData(r.data)).catch(() => {});
  }, []);
  if (!data || data.count === 0) return null;
  return (
    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 space-y-2" data-testid="anomalies-card">
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
        <p className="text-xs font-bold text-red-400">{data.count} valeur(s) impossible(s) détectée(s) — à corriger</p>
      </div>
      <div className="space-y-1">
        {data.anomalies.slice(0, 10).map((a, i) => (
          <p key={i} className="text-[11px] text-zinc-300 font-mono" data-testid={`anomalie-${a.nom}-${a.field}`}>
            <span className="text-white font-bold">{a.nom}</span> · {a.where} · {FIELD_LABELS[a.field] || a.field} = <span className="text-red-400 font-bold">{a.value}</span> <span className="text-zinc-500">(attendu {a.bounds})</span>
          </p>
        ))}
        {data.count > 10 && <p className="text-[10px] text-zinc-500">+{data.count - 10} autres…</p>}
      </div>
      <p className="text-[10px] text-zinc-500">Corrige-les dans la grille du palier concerné ou l'historique des pesées intermédiaires.</p>
    </div>
  );
}

// ─── Export Excel de toutes les données du challenge ────────────────────────
function ExportButton() {
  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await apiClient.get('/challenge/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `challenge_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export Excel téléchargé');
    } catch { toast.error("Erreur lors de l'export"); }
    finally { setExporting(false); }
  };
  return (
    <button onClick={handleExport} disabled={exporting} data-testid="export-challenge-btn"
      className="w-full flex items-center justify-center gap-2 py-2.5 bg-[#1B1B1B] border border-[#FF6B35]/40 rounded-lg text-xs font-bold text-[#FF6B35] hover:bg-[#FF6B35]/10 transition-colors disabled:opacity-50">
      <Download className="w-4 h-4" /> {exporting ? 'Export en cours…' : 'Exporter toutes les données (Excel)'}
    </button>
  );
}

export function ChallengeGestionTab() {
  const [section, setSection] = useState('saisie');
  const [participants, setParticipants] = useState([]);
  const [paliers, setPaliers] = useState([]);
  const [palierDates, setPalierDates] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [pRes, palRes, dRes] = await Promise.allSettled([
        apiClient.get('/challenge/participants'),
        apiClient.get('/challenge/paliers'),
        apiClient.get('/challenge/pesees-dates'),
      ]);
      if (pRes.status === 'fulfilled') setParticipants(pRes.value.data.filter(p => p.actif || p.caution_p1_ok));
      if (palRes.status === 'fulfilled') setPaliers(palRes.value.data);
      if (dRes.status === 'fulfilled') setPalierDates(dRes.value.data.dates || []);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <Spinner />;

  const sections = [
    { id: 'saisie', label: 'Saisie paliers', icon: Scale },
    { id: 'intermediaire', label: 'Pesee inter.', icon: Plus },
    { id: 'controle', label: 'Controle', icon: Shield },
    { id: 'pronostics', label: 'Pronostics', icon: Trophy },
  ];

  return (
    <div className="p-4 pb-24 space-y-4" data-testid="gestion-challenge-tab">
      {/* Anomalies de saisie */}
      <AnomaliesCard />

      {/* Export complet */}
      <ExportButton />

      {/* Section nav */}
      <div className="flex gap-1">
        {sections.map(s => (
          <button key={s.id} onClick={() => setSection(s.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-bold transition-all ${
              section === s.id ? 'bg-[#FF6B35] text-white' : 'bg-[#1B1B1B] border border-[#2A2A2E] text-zinc-400 hover:border-zinc-500'
            }`} data-testid={`gestion-section-${s.id}`}>
            <s.icon className="w-3.5 h-3.5" /> {s.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {section === 'saisie' && <SaisiePaliers participants={participants} paliers={paliers} palierDates={palierDates} onRefresh={fetchData} />}
      {section === 'intermediaire' && <PeseeIntermediaire participants={participants} onRefresh={fetchData} />}
      {section === 'controle' && <ControleSection />}
      {section === 'pronostics' && <PronosticsCautionsAdmin onRefresh={fetchData} />}
    </div>
  );
}
